import { useCallback, useRef, useState } from "react";
import type { ChangeEvent, DragEvent } from "react";
import { Upload } from "lucide-react";
import { cn } from "@/design-system/lib/utils";

// LLM & Reports v3.2 §8.1 step 4 — drag-and-drop zone that accepts
// files from the browser and forwards each one to the parent's
// onUpload callback. Doesn't perform the actual upload itself —
// keeping the network call in the parent / a hook makes the dropzone
// reusable for non-report contexts and easier to test.
//
// Accepts every MIME by default; the backend is the authoritative
// gate (it returns ConversionStatus = Skipped on unsupported types,
// which the parent surfaces as a per-row warning).

export interface DocumentUploadDropzoneProps {
  onFiles: (files: File[]) => void;
  disabled?: boolean;
  /** Hint text shown inside the dropzone. Defaults to a generic prompt. */
  hint?: string;
  /** Comma-separated MIME / extension hints for the native picker. */
  accept?: string;
  className?: string;
}

export function DocumentUploadDropzone({
  onFiles,
  disabled = false,
  hint = "Drop files here or click to browse",
  accept,
  className,
}: DocumentUploadDropzoneProps) {
  const [isDragOver, setIsDragOver] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const handleFiles = useCallback(
    (fileList: FileList | null) => {
      if (!fileList || fileList.length === 0) return;
      onFiles(Array.from(fileList));
    },
    [onFiles],
  );

  const handleDragOver = (event: DragEvent<HTMLLabelElement>) => {
    if (disabled) return;
    event.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (event: DragEvent<HTMLLabelElement>) => {
    event.preventDefault();
    setIsDragOver(false);
  };

  const handleDrop = (event: DragEvent<HTMLLabelElement>) => {
    if (disabled) return;
    event.preventDefault();
    setIsDragOver(false);
    handleFiles(event.dataTransfer.files);
  };

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    handleFiles(event.target.files);
    // Reset so the same file can be re-selected (e.g. after a Failed
    // conversion the user wants to retry by re-uploading).
    if (inputRef.current) inputRef.current.value = "";
  };

  return (
    <label
      className={cn(
        "block cursor-pointer rounded-md border border-dashed",
        "px-space-4 py-space-6 text-center transition-colors",
        "border-border-light hover:border-gold-shadow hover:bg-white-warm",
        isDragOver && "border-gold-shadow bg-white-warm",
        disabled && "cursor-not-allowed opacity-60",
        className,
      )}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      <input
        ref={inputRef}
        type="file"
        multiple
        accept={accept}
        disabled={disabled}
        className="sr-only"
        onChange={handleChange}
      />
      <Upload size={24} className="mx-auto mb-space-2 text-gold-shadow" aria-hidden />
      <p className="font-body text-body text-onyx">{hint}</p>
      <p className="font-body text-caption text-gray-mid mt-space-1">
        Word, Excel, PDF, or images up to 32 MB each.
      </p>
    </label>
  );
}
